import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDashboard } from "@/hooks/useProgress";
import { useChapters, useSubjects } from "@/hooks/useCurriculum";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import type { UserProgress } from "@/lib/api-types";

export type DailyQuestion = {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation?: string;
  chapterId: string;
  chapterName: string;
  subjectId: string;
  /** Where this question came from -- drives the hook line tone. */
  source: "completed" | "touched" | "discovery";
};

export type DailyQuestionState =
  | { kind: "loading" }
  | { kind: "empty" } // genuinely nothing we can show (e.g. user has no class)
  | { kind: "ready"; question: DailyQuestion; alreadyTakenIndex: number | null };

const HOOK_VARIANTS = [
  "One question for you, {name}?",
  "Quick check?",
  "Still got it?",
  "Bet you remember this?",
  "Pop quiz — just one.",
  "Remember this from {chapter}?",
  "Brain teaser ready?",
  "Today's check-in.",
] as const;

/** Hooks specifically for the discovery path (new users / no completed yet). */
const DISCOVERY_HOOKS = [
  "Try a sample question?",
  "Warm up with this one?",
  "Five seconds. One MCQ.",
  "Curious? Try this.",
] as const;

const COMPLETION_THRESHOLD = 80;

/**
 * Picks ONE deterministic question per day per user. Pool priority:
 *
 *   1. Completed chapters (`is_completed === true` OR percent >= 80).
 *   2. Touched chapters (in dashboard.recent_chapters but not completed).
 *   3. Discovery: the first demo chapter of a subject in the user's class.
 *      Used for brand-new users so they always get a sample question.
 *
 * Same locked question for the whole calendar day. After the user answers
 * it, `pickRandom()` enters practice mode -- a randomised one-off pulled
 * from the same expanded pool.
 */
export function useDailyQuestion(opts: {
  userId: string | null;
  classId: string | null;
  studentName: string | null;
}): DailyQuestionState & {
  hookLine: string;
  pickRandom: () => void;
  markTaken: (pickedIndex: number) => void;
  /** Used by the "almost there" empty state if we end up there anyway. */
  mostProgressed: UserProgress | null;
} {
  const { userId, classId, studentName } = opts;
  const dashboard = useDashboard();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const seed = useMemo(() => hashSeed(`${today}::${userId ?? "anon"}`), [today, userId]);

  const recent = useMemo(() => dashboard.data?.recent_chapters ?? [], [dashboard.data]);
  const completed = useMemo(
    () => recent.filter((p) => p.is_completed === true || p.chapter_completion_percent >= COMPLETION_THRESHOLD),
    [recent],
  );
  const touched = useMemo(
    () => recent.filter((p) => !(p.is_completed === true || p.chapter_completion_percent >= COMPLETION_THRESHOLD)),
    [recent],
  );
  const mostProgressed = useMemo(() => {
    if (recent.length === 0) return null;
    return recent.slice().sort((a, b) => b.chapter_completion_percent - a.chapter_completion_percent)[0];
  }, [recent]);

  // Practice mode seed -- bumped by pickRandom().
  const [practiceSeed, setPracticeSeed] = useState<number | null>(null);
  const effectiveSeed = practiceSeed ?? seed;

  // ---------- Discovery fallback: a demo chapter from the user's class.
  // Pull subjects for the class, pick one deterministically, then pull
  // its chapters and pick the first demo chapter (typically chapter-1).
  // Only runs if priority 1 and 2 are empty (recent_chapters is empty).
  const needsDiscovery = !dashboard.isLoading && recent.length === 0;
  const subjectList = useSubjects(needsDiscovery ? (classId ?? undefined) : undefined);
  const discoverySubjectId = useMemo(() => {
    const subs = subjectList.data ?? [];
    if (subs.length === 0) return undefined;
    return subs[effectiveSeed % subs.length].subject_id;
  }, [subjectList.data, effectiveSeed]);
  const discoveryChapters = useChapters(discoverySubjectId);
  const discoveryChapter = useMemo(() => {
    const chs = discoveryChapters.data ?? [];
    const demos = chs.filter((c) => c.is_demo === true);
    if (demos.length === 0) return null;
    return demos[effectiveSeed % demos.length];
  }, [discoveryChapters.data, effectiveSeed]);

  // ---------- Pick the active chapter from the highest-priority pool.
  type Pick = {
    chapter_id: string;
    subject_id: string;
    chapter_name: string | null;
    source: DailyQuestion["source"];
  };

  const picked: Pick | null = useMemo(() => {
    if (completed.length > 0) {
      const ranked = completed
        .slice()
        .sort((a, b) => new Date(b.last_accessed_at).getTime() - new Date(a.last_accessed_at).getTime());
      const idx = weightedIndex(ranked.length, effectiveSeed);
      const p = ranked[idx];
      return { chapter_id: p.chapter_id, subject_id: p.subject_id, chapter_name: null, source: "completed" };
    }
    if (touched.length > 0) {
      const ranked = touched
        .slice()
        .sort((a, b) => new Date(b.last_accessed_at).getTime() - new Date(a.last_accessed_at).getTime());
      const idx = weightedIndex(ranked.length, effectiveSeed);
      const p = ranked[idx];
      return { chapter_id: p.chapter_id, subject_id: p.subject_id, chapter_name: null, source: "touched" };
    }
    if (discoveryChapter) {
      return {
        chapter_id: discoveryChapter.chapter_id,
        subject_id: discoveryChapter.subject_id,
        chapter_name: discoveryChapter.chapter_name,
        source: "discovery",
      };
    }
    return null;
  }, [completed, touched, discoveryChapter, effectiveSeed]);

  // ---------- Resolve chapter name when the pick came from progress
  // (which only has IDs, not the friendly name).
  const subjectChapters = useChapters(picked?.source !== "discovery" ? picked?.subject_id : undefined);
  const resolvedName = useMemo(() => {
    if (!picked) return "";
    if (picked.chapter_name) return picked.chapter_name;
    return subjectChapters.data?.find((c) => c.chapter_id === picked.chapter_id)?.chapter_name
      ?? "this chapter";
  }, [picked, subjectChapters.data]);

  // ---------- Fetch the chosen quiz.json + pick a question.
  const signed = useSignedUrl({ chapterId: picked?.chapter_id, assetType: "quiz" });

  const quiz = useQuery({
    queryKey: ["daily-question-quiz", signed.url ?? null],
    enabled: !!signed.url,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(signed.url!);
      if (!res.ok) throw new Error("quiz fetch failed");
      const raw = (await res.json()) as unknown;
      if (!Array.isArray(raw)) return [] as RawQuestion[];
      return raw.filter(isRawQuestion);
    },
    retry: 0,
  });

  const composedQuestion = useMemo<DailyQuestion | null>(() => {
    if (!picked || !quiz.data || quiz.data.length === 0) return null;
    const q = quiz.data[effectiveSeed % quiz.data.length];
    return {
      question: q.question,
      options: q.options,
      correctAnswer: q.correctAnswer,
      explanation: q.explanation,
      chapterId: picked.chapter_id,
      chapterName: resolvedName,
      subjectId: picked.subject_id,
      source: picked.source,
    };
  }, [picked, quiz.data, resolvedName, effectiveSeed]);

  // ---------- "Taken today" persistence -- only for the locked daily
  // (NOT practice-mode answers). Storage is per-day per-user.
  const takenKey = userId ? `staxed:daily-q-taken:${today}:${userId}` : null;
  const [takenIndex, setTakenIndex] = useState<number | null>(() => {
    if (typeof window === "undefined" || !takenKey) return null;
    const raw = localStorage.getItem(takenKey);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  });
  useEffect(() => { setTakenIndex(null); }, [today]);

  const markTaken = (pickedIndex: number) => {
    if (practiceSeed != null) return; // practice mode doesn't lock
    if (takenKey) localStorage.setItem(takenKey, String(pickedIndex));
    setTakenIndex(pickedIndex);
  };

  const pickRandom = () => {
    setPracticeSeed(Math.floor(Math.random() * 0xfffffff));
  };

  // ---------- Hook line composition. Title-case the first letter so
  // we never address the user as "usmaan?" -- matches firstName() in
  // lib/format used elsewhere on the dashboard.
  const fn = (() => {
    const raw = studentName?.trim().split(/\s+/)[0];
    if (!raw) return "you";
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  })();
  const useName = userId ? (hashSeed(`hook::${userId}::${today}`) % 10) < 6 : false;
  const hookSeed = hashSeed(`hook-line::${userId ?? "anon"}::${today}`);
  const hookLine = useMemo(() => {
    // Discovery path gets its own gentler pool.
    if (picked?.source === "discovery") {
      return DISCOVERY_HOOKS[hookSeed % DISCOVERY_HOOKS.length];
    }
    const tpl = HOOK_VARIANTS[hookSeed % HOOK_VARIANTS.length];
    let s: string = tpl;
    if (s.includes("{name}")) {
      s = useName && fn !== "you"
        ? s.replace("{name}", fn)
        : s.replace(/,\s*\{name\}/g, "").replace(/\s*\{name\}/g, "");
    }
    if (s.includes("{chapter}")) {
      s = resolvedName
        ? s.replace("{chapter}", resolvedName)
        : s.replace(/\s*from\s*\{chapter\}/, "").replace(/\s*\{chapter\}/g, "");
    }
    return s;
  }, [picked, hookSeed, useName, fn, resolvedName]);

  // ---------- Compose public state.
  let state: DailyQuestionState;
  if (
    dashboard.isLoading ||
    (needsDiscovery && (subjectList.isLoading || discoveryChapters.isLoading)) ||
    (picked && (quiz.isLoading || subjectChapters.isLoading))
  ) {
    state = { kind: "loading" };
  } else if (!composedQuestion) {
    state = { kind: "empty" };
  } else {
    state = { kind: "ready", question: composedQuestion, alreadyTakenIndex: takenIndex };
  }

  return { ...state, hookLine, pickRandom, markTaken, mostProgressed };
}

// ---------------------------------------------------------------------------
function weightedIndex(n: number, seed: number): number {
  // Recency-weighted: rank 0 gets weight n, rank n-1 gets weight 1.
  // This makes the most recently accessed chapter ~n times as likely
  // to surface as the oldest, while still rotating over time.
  const weights = Array.from({ length: n }, (_, i) => Math.max(1, n - i));
  const total = weights.reduce((a, b) => a + b, 0);
  let r = seed % total;
  for (let i = 0; i < n; i++) {
    r -= weights[i];
    if (r < 0) return i;
  }
  return 0;
}

type RawQuestion = {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation?: string;
};

function isRawQuestion(x: unknown): x is RawQuestion {
  if (!x || typeof x !== "object") return false;
  const o = x as Partial<RawQuestion>;
  return (
    typeof o.question === "string" &&
    Array.isArray(o.options) &&
    o.options.every((s) => typeof s === "string") &&
    typeof o.correctAnswer === "number" &&
    o.correctAnswer >= 0 &&
    o.correctAnswer < (o.options?.length ?? 0)
  );
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}
